'use client';

import { Add, Delete } from "@mui/icons-material";
import { Box, Button, FormHelperText, IconButton, InputBase, Stack } from "@mui/material";
import { useEffect, useRef } from "react";

export type PhoneEditorValue = {
    id?: string;
    number?: string | null;
    reachabilityHint?: string | null;
};

export type PhoneEditorProps = {
    value?: PhoneEditorValue | null;
    onChange: (value: PhoneEditorValue | undefined) => void;
    numberError?: string | null;
    reachabilityHintError?: string | null;
}

export default function PhoneEditor({
    value,
    onChange,
    numberError,
    reachabilityHintError,
}: PhoneEditorProps) {
    const hasValue = value !== null && value !== undefined;
    const numberInputRef = useRef<HTMLInputElement | null>(null);
    const prevHasValueRef = useRef(hasValue);
    const hasError = Boolean(numberError || reachabilityHintError);
    const helperText = [numberError, reachabilityHintError].filter(Boolean).join(" ");

    useEffect(() => {
        if (!prevHasValueRef.current && hasValue) {
            numberInputRef.current?.focus();
        }
        prevHasValueRef.current = hasValue;
    }, [hasValue]);

    if (!hasValue) {
        return (
            <Button
                variant="outlined"
                color="breakout"
                startIcon={<Add />}
                onClick={() => onChange({ id: 'new', number: '', reachabilityHint: '' })}
                fullWidth
            >
                Telefon hinzufügen
            </Button>
        );
    }

    const nextValue: PhoneEditorValue = {
        id: value.id ?? 'new',
        number: value.number ?? '',
        reachabilityHint: value.reachabilityHint ?? '',
    };

    return (
        <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'flex-start' }}>
            <Box sx={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid",
                        borderColor: hasError ? "error.main" : "var(--breakout)",
                        borderRadius: 1,
                        minHeight: 40,
                        px: 1.5,
                        "&:focus-within": {
                            borderColor: hasError ? "error.main" : "primary.main",
                            boxShadow: (theme) =>
                                hasError ? `0 0 0 1px ${theme.palette.error.main}` : `0 0 0 1px ${theme.palette.primary.main}`,
                        },
                    }}
                >
                    <InputBase
                        inputRef={numberInputRef}
                        value={nextValue.number}
                        onChange={(event) =>
                            onChange({
                                ...nextValue,
                                number: event.target.value,
                            })
                        }
                        placeholder="Telefon"
                        inputProps={{ "aria-label": "Telefon" }}
                        sx={{ flex: "0 0 45%" }}
                    />
                    <Box sx={{ display: "flex", flex: "1 1 auto", borderLeft: "1px solid", borderColor: "divider", pl: 1.5, ml: 1.5 }}>
                        <InputBase
                            value={nextValue.reachabilityHint}
                            onChange={(event) =>
                                onChange({
                                    ...nextValue,
                                    reachabilityHint: event.target.value,
                                })
                            }
                            placeholder="Erreichbarkeit"
                            inputProps={{ "aria-label": "Erreichbarkeit" }}
                            sx={{ flex: "1 1 auto" }}
                        />
                    </Box>
                </Box>
                {helperText ? (
                    <FormHelperText error={hasError} sx={{ mx: 1.5 }}>
                        {helperText}
                    </FormHelperText>
                ) : null}
            </Box>
            <IconButton
                aria-label="Telefon entfernen"
                color="error"
                onClick={() => onChange(undefined)}
                sx={{ width: 40, height: 40, alignSelf: 'flex-start' }}
            >
                <Delete sx={{ fontSize: 28 }} />
            </IconButton>
        </Stack>
    );
}
